import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { PUT_URL } from '@utils/api/endpoints.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import type { Connection } from '@utils/types/connections/connection.ts';
import type { BaseError } from '@utils/types/shared/base.ts';

/**
 * Per-connection YouTube settings (ticket #4101 follow-up).
 *
 * The previous iteration stored the opt-in on the organization, which
 * forced a single policy across every YouTube channel the org had
 * connected. We moved it to the connection row itself so an org can
 * run a captions-only connection next to an upload-capable one.
 */
export interface ConnectionSettings {
  /** When off, the YouTube new-video path is rejected at both the API and worker guards. */
  video_uploads_enabled: boolean;
}

interface SettingsEnvelope {
  result: ConnectionSettings;
}

async function updateConnectionSettings(
  connectionId: string,
  next: ConnectionSettings
): Promise<ConnectionSettings> {
  const { data } = await Axios.put<SettingsEnvelope>(
    PUT_URL(connectionId).connectionSettings,
    next
  );
  return data.result;
}

/**
 * Mutation hook used by the per-row toggle in the Connections list.
 * The optimistic path flips the connection list cache immediately so
 * the switch feels instant; the server's response is the source of
 * truth on settle (`invalidateQueries`).
 */
export function useUpdateConnectionSettings(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    ConnectionSettings,
    AxiosError<BaseError>,
    ConnectionSettings
  >(next => updateConnectionSettings(connectionId, next), {
    onMutate: async next => {
      await queryClient.cancelQueries(QueryKeys.connections());
      const previous = queryClient.getQueryData<Connection[]>(
        QueryKeys.connections()
      );
      if (previous) {
        queryClient.setQueryData<Connection[]>(
          QueryKeys.connections(),
          previous.map(c =>
            c.id === connectionId
              ? { ...c, video_uploads_enabled: next.video_uploads_enabled }
              : c
          )
        );
      }
      return { previous } as const;
    },
    onError: (_err, _next, ctx) => {
      const previous = (ctx as { previous?: Connection[] } | undefined)
        ?.previous;
      if (previous) {
        queryClient.setQueryData(QueryKeys.connections(), previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(QueryKeys.connections());
    },
    useErrorBoundary: false
  });
}
